/**
 * useTableSort — sort client-side controlado pro <DataTable/> compartilhado.
 *
 * Uso:
 *   const { sorted, sort, toggle } = useTableSort(items, { key: 'date', order: 'desc' });
 *   <DataTable columns={cols /* com sortKey nas colunas sortaveis * /}
 *              rows={sorted} sort={sort} onSort={toggle} ... />
 *
 * O DataTable so torna o header clicavel quando a coluna tem `sortKey` E `onSort`
 * e' passado. A comparacao acessa row[sortKey] (numero -> numerico; resto ->
 * localeCompare pt-BR numeric). null/undefined vao pro fim.
 */
import { useMemo, useState } from 'react';

// undefined (nao null) pra casar com a prop `sort?` do <DataTable/>.
export type SortState = { key: string; order: 'asc' | 'desc' } | undefined;

export function useTableSort<T extends Record<string, any>>(
  items: T[],
  initial: SortState = undefined,
) {
  const [sort, setSort] = useState<SortState>(initial);

  const toggle = (key: string) =>
    setSort((s) =>
      s && s.key === key
        ? { key, order: s.order === 'asc' ? 'desc' : 'asc' }
        : { key, order: 'asc' },
    );

  const sorted = useMemo(() => {
    if (!sort) return items;
    const { key, order } = sort;
    const dir = order === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'pt-BR', { numeric: true }) * dir;
    });
  }, [items, sort]);

  return { sorted, sort, toggle };
}
